import type { Chip } from "../core/chip";
import { ChipNode } from "./ChipNode";
import { RoutePath } from "./RoutePath";

type Props = {
  chips: Chip[];
  activeChipId: string;
  onSelectChip: (id: string) => void;
};

/**
 * Gate 2C — CHIPMAP COMPOSITION PARITY
 *
 * Content is authored in a PORTRAIT design space matching the map band aspect
 * (~0.72–0.75). Tight viewBox + xMidYMid meet fills the band without large
 * upper/lower functional dead zones. Side insets keep nodes fully visible.
 */

/**
 * Portrait design space tuned to Main.jpg diamond.
 * Aspect ≈ 0.72 matches map-band (390/540, 896/1250) so meet does not
 * letterbox the functional graph on tall viewports.
 */
const PAD = 22;
const VW = 720;
/** Taller VH removes 896 vertical letterboxing while keeping 390 density. */
const VH = 1000;

const procW = 200;
const procH = 212;
const bookW = 222;
const bookH = 188;
/** Target ~136–140px at 390 when width maps fully. */
const cpuS = 255;
const cpuC = cpuS * 0.20710678118;

const cpuCx = VW / 2;
const cpuCy = 508;

const positions: Record<string, { x: number; y: number }> = {
  arcopedia: { x: VW / 2, y: PAD + bookH / 2 + 4 },
  loremine: { x: PAD + procW / 2, y: 270 },
  artemis: { x: VW - PAD - procW / 2, y: 270 },
  build: { x: PAD + procW / 2, y: VH - PAD - procH / 2 - 4 },
  verify: { x: VW - PAD - procW / 2, y: VH - PAD - procH / 2 - 4 },
};

function regularOctagon(
  cx: number,
  cy: number,
  size: number,
  cut: number,
): string {
  const h = size / 2;
  const l = cx - h;
  const r = cx + h;
  const t = cy - h;
  const b = cy + h;
  return `M ${l + cut} ${t} L ${r - cut} ${t} L ${r} ${t + cut} L ${r} ${b - cut} L ${r - cut} ${b} L ${l + cut} ${b} L ${l} ${b - cut} L ${l} ${t + cut} Z`;
}

function nodeBox(id: string): { w: number; h: number } {
  return id === "arcopedia" ? { w: bookW, h: bookH } : { w: procW, h: procH };
}

const VIEW_MARGIN = 8;
const viewBox = `${-VIEW_MARGIN} ${-VIEW_MARGIN} ${VW + VIEW_MARGIN * 2} ${VH + VIEW_MARGIN * 2}`;

export function ChipMap({ chips, activeChipId, onSelectChip }: Props) {
  const active = chips.find((chip) => chip.id === activeChipId) ?? chips[0];
  const colors = {
    RECOMMENDED: "var(--gold)",
    EXPLORATORY: "var(--purple)",
    AVAILABLE: "var(--cyan)",
  };

  const cpuHalf = cpuS / 2;
  const cpuJunctions: Record<string, { x: number; y: number }> = {
    ARCOPEDIA: { x: cpuCx, y: cpuCy - cpuHalf },
    LOREMINE: { x: cpuCx - cpuHalf * 0.74, y: cpuCy - cpuHalf * 0.42 },
    ARTEMIS: { x: cpuCx + cpuHalf * 0.74, y: cpuCy - cpuHalf * 0.42 },
    BUILD: { x: cpuCx - cpuHalf * 0.74, y: cpuCy + cpuHalf * 0.42 },
    VERIFY: { x: cpuCx + cpuHalf * 0.74, y: cpuCy + cpuHalf * 0.42 },
  };

  const outer = regularOctagon(cpuCx, cpuCy, cpuS, cpuC);
  const mid = regularOctagon(cpuCx, cpuCy, cpuS * 0.86, cpuC * 0.86);
  const inner = regularOctagon(cpuCx, cpuCy, cpuS * 0.72, cpuC * 0.72);

  return (
    <svg
      class="map"
      viewBox={viewBox}
      role="img"
      aria-label="DeckOS CHIPMAP"
      preserveAspectRatio="xMidYMid meet"
    >
      <g
        class="map-atmosphere"
        opacity="0.38"
        style={{ pointerEvents: "none" }}
      >
        <path
          d={`M${PAD} 90 H${VW / 2 - 90} M${VW / 2 + 90} 90 H${VW - PAD}
             M${PAD} ${VH - 90} H${VW / 2 - 90} M${VW / 2 + 90} ${VH - 90} H${VW - PAD}
             M90 ${PAD} V180 M${VW - 90} ${PAD} V180
             M90 ${VH - 180} V${VH - PAD} M${VW - 90} ${VH - 180} V${VH - PAD}`}
          fill="none"
          stroke="rgba(39,215,255,0.15)"
          stroke-width="1"
        />
        <rect
          x={PAD + 10}
          y={VH / 2 - 20}
          width="30"
          height="30"
          fill="none"
          stroke="rgba(39,215,255,0.08)"
        />
        <rect
          x={VW - PAD - 40}
          y={VH / 2 - 20}
          width="30"
          height="30"
          fill="none"
          stroke="rgba(217,118,255,0.08)"
        />
        <circle
          cx={PAD + 36}
          cy={PAD + 20}
          r="2"
          fill="rgba(39,215,255,0.28)"
        />
        <circle
          cx={VW - PAD - 36}
          cy={PAD + 20}
          r="2"
          fill="rgba(217,118,255,0.26)"
        />
      </g>

      <g class="functional-graph">
        <g class="routes" style={{ pointerEvents: "none" }}>
          {active.routes.map((route) => {
            const targetId = route.targetChipId
              ? route.targetChipId
              : route.invoke
                ? route.invoke.toLowerCase()
                : undefined;
            if (!targetId || !positions[targetId]) return null;
            const target = positions[targetId];
            const box = nodeBox(targetId);
            const color =
              colors[route.state as keyof typeof colors] ?? "var(--cyan)";
            const name = route.invoke ?? route.label;
            const destination =
              targetId === "arcopedia"
                ? { x: target.x, y: target.y + box.h / 2 }
                : targetId === "loremine"
                  ? { x: target.x + box.w / 2, y: target.y }
                  : targetId === "artemis"
                    ? { x: target.x - box.w / 2, y: target.y }
                    : targetId === "build"
                      ? {
                          x: target.x + box.w / 2,
                          y: target.y - box.h * 0.12,
                        }
                      : {
                          x: target.x - box.w / 2,
                          y: target.y - box.h * 0.12,
                        };
            return (
              <RoutePath
                key={route.id}
                from={cpuJunctions[name] ?? { x: cpuCx, y: cpuCy }}
                to={destination}
                color={color}
                dashed={
                  name === "VERIFY" ? false : route.state !== "RECOMMENDED"
                }
              />
            );
          })}
        </g>

        {chips
          .filter((chip) => chip.id !== active.id && positions[chip.id])
          .map((chip) => {
            const p = positions[chip.id];
            const box = nodeBox(chip.id);
            const route = active.routes.find(
              (r) =>
                r.targetChipId === chip.id ||
                r.invoke?.toLowerCase() === chip.id,
            );
            return (
              <ChipNode
                key={chip.id}
                chip={chip}
                x={p.x - box.w / 2}
                y={p.y - box.h / 2}
                width={box.w}
                height={box.h}
                color={
                  colors[route?.state as keyof typeof colors] ?? "var(--cyan)"
                }
                onSelect={onSelectChip}
              />
            );
          })}

        <g class="active-chip" role="img" aria-label="CPU-001 active chip">
          <path
            d={outer}
            fill="rgba(5,14,27,.97)"
            stroke="#f5f8ff"
            stroke-width="5.5"
            style={{
              filter: "drop-shadow(0 0 10px rgba(245,248,255,0.35))",
            }}
          />
          <path
            d={mid}
            fill="none"
            stroke="var(--cyan)"
            stroke-width="3.5"
            style={{
              filter: "drop-shadow(0 0 8px rgba(39,215,255,0.55))",
            }}
          />
          <path
            d={inner}
            fill="none"
            stroke="#eaf3ff"
            stroke-width="1.9"
            opacity=".88"
          />
          <path
            d={`M${cpuCx} ${cpuCy - 60} l34 19 v38 l-34 19 -34-19 v-38z M${cpuCx} ${cpuCy + 16} v-38 M${cpuCx - 34} ${cpuCy - 41} l34 19 34-19`}
            fill="none"
            stroke="#f5f8ff"
            stroke-width="2.1"
          />
          <text
            x={cpuCx}
            y={cpuCy + 54}
            text-anchor="middle"
            class="active-label"
          >
            CPU-001
          </text>
          <text
            x={cpuCx}
            y={cpuCy + 82}
            text-anchor="middle"
            class="active-subtitle"
          >
            GAME TWO CPU ENCOUNTER
          </text>
          <text
            x={cpuCx}
            y={cpuCy + 108}
            text-anchor="middle"
            class="active-revision"
          >
            REV 12
          </text>
        </g>
      </g>
    </svg>
  );
}
